import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { Wordmark } from '../components/Brand';
import { Icon, type IconName } from '../components/Icon';
import { LANGUAGES, type MahalluChoice, type OtpChannel, type OtpStartResponse, type SignInStepResponse, type TotpSetupResponse } from '@mahallu/shared';
import { ApiError, auth, consoleApi, markUrl, useMe, usePublicMahallu } from '../api';
import { mahalluLink } from '../mahalluLink';
import { setLanguage } from '../i18n';

type Step = { kind: 'identify' } | { kind: 'code'; challenge: OtpStartResponse } | { kind: 'choose'; mahallus: MahalluChoice[] } | { kind: 'mfa'; enrolled: boolean };

const looksLikeEmail = (s: string) => s.includes('@');

/** Country codes offered first: Kerala, then the Gulf countries where most pravasi members live. */
const COUNTRY_CODES = ['+91', '+971', '+966', '+974', '+968', '+965', '+973'] as const;

/**
 * Sign-in for the committee app and, with mode="console", for the OptiFi console (ADR-0040). On a mahallu link
 * (`<slug>.rabitaone.in`, or `?m=<slug>` locally) the page shows that mahallu and opens it after the code.
 */
export function SignIn({ mode = 'app' }: { mode?: 'app' | 'console' }) {
  const isConsole = mode === 'console';
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const me = useMe();
  const [step, setStep] = useState<Step>({ kind: 'identify' });
  const [identifier, setIdentifier] = useState('');
  const [by, setBy] = useState<'PHONE' | 'EMAIL'>('PHONE');
  const [cc, setCc] = useState<string>('+91');
  // A number typed with its own + code wins over the picker; a leading 0 is dropped.
  const fullIdentifier = by === 'EMAIL' ? identifier.trim() : identifier.trim().startsWith('+') ? identifier.trim() : `${cc}${identifier.replace(/\D/g, '').replace(/^0+/, '')}`;
  const [channel, setChannel] = useState<OtpChannel>('WHATSAPP');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const next = (location.state as { from?: string } | null)?.from ?? (isConsole ? '/console' : '/');
  const [link] = useState(() => (isConsole ? null : mahalluLink()));
  const linked = usePublicMahallu(link);

  // Coming back mid-way (second factor or mahallu choice still open): resume at that step.
  useEffect(() => {
    if (!me.data || step.kind !== 'identify') return;
    if (isConsole) {
      if (me.data.console && me.data.mfa.state === 'PENDING') setStep({ kind: 'mfa', enrolled: me.data.mfa.enrolled });
      return;
    }
    if (me.data.console) return;
    if (me.data.mfa.state === 'PENDING') setStep({ kind: 'mfa', enrolled: me.data.mfa.enrolled });
    else if (!me.data.mahallu) setStep({ kind: 'choose', mahallus: me.data.mahallus });
  }, [me.data, step.kind, isConsole]);

  useEffect(() => {
    if (wait <= 0) return;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  if (isConsole && me.data?.console && me.data.mfa.state === 'VERIFIED') return <Navigate to={next} replace />;
  if (!isConsole && me.data && me.data.mfa.state !== 'PENDING' && me.data.mahallu) return <Navigate to={next} replace />;

  const explain = (e: unknown) => {
    if (e instanceof ApiError) {
      if (e.code === 'CODE_WRONG') return t('signin.codeWrong', { n: e.attemptsLeft ?? 0 });
      if (e.code === 'CODE_EXPIRED') return t('signin.codeExpired');
      if (e.code === 'RATE_LIMITED') return t('signin.tooMany');
      if (e.status < 500) return e.message;
    }
    return t('signin.error');
  };

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(explain(e));
    } finally {
      setBusy(false);
    }
  }

  async function afterStep(res: SignInStepResponse) {
    setCode('');
    if (res.status === 'SIGNED_IN') {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.removeQueries({ queryKey: ['houses'] });
      navigate(next, { replace: true });
    } else if (res.status === 'MFA_REQUIRED') setStep({ kind: 'mfa', enrolled: Boolean(res.enrolled) });
    else if (link && res.mahallus?.some((m) => m.slug === link)) await afterStep(await auth.chooseMahallu(link));
    else setStep({ kind: 'choose', mahallus: res.mahallus ?? [] });
  }

  const send = (via?: OtpChannel) =>
    run(async () => {
      const chosen = by === 'EMAIL' || looksLikeEmail(identifier) ? 'EMAIL' : (via ?? channel);
      const challenge = await auth.start(fullIdentifier, chosen);
      setChannel(challenge.channel);
      setCode('');
      setWait(challenge.resendInSeconds);
      setStep({ kind: 'code', challenge });
    });

  const onIdentify = (e: FormEvent) => {
    e.preventDefault();
    void send();
  };
  const onCode = (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'code') return;
    void run(async () => {
      const res = await auth.verify(step.challenge.challengeId, code);
      // The console always asks for the authenticator again; someone who is not an operator is refused there.
      await afterStep(isConsole ? await consoleApi.enter() : res);
    });
  };

  const visualTitle = isConsole ? t('console.title') : linked.data ? linked.data.nameEn : t('signin.visualTitle');
  const visualBody = isConsole ? t('console.signin.visual') : t('signin.visual');

  return (
    <div className="auth-split">
      <aside className="auth-visual">
        <div className="av-glow" aria-hidden="true" />
        <Wordmark light full />
        <div className="av-copy">
          {linked.data?.markHash && !isConsole ? <img className="av-mark" src={markUrl(linked.data.slug, linked.data.markHash)} alt="" /> : null}
          {linked.data?.nameMl && !isConsole ? <span className="ml av-kicker">{linked.data.nameMl}</span> : <span className="av-kicker">{t('app.descriptor')}</span>}
          <h2>{visualTitle}</h2>
          <p>{visualBody}</p>
        </div>
        <figure className="av-preview" aria-hidden="true">
          <div className="av-bar"><i /><i /><i /></div>
          <img src={i18n.language === 'ml' ? '/brand/preview-register-ml.webp' : '/brand/preview-register.webp'} alt="" />
        </figure>
        <ul className="av-trust">
          <li>{t('signin.trust1')}</li>
          <li>{t('signin.trust2')}</li>
          <li>{t('signin.trust3')}</li>
        </ul>
      </aside>
      <div className="auth-wrap">
      <div className="auth-top">
        <a className="auth-back" href="https://rabitaone.in"><Icon name="back" />rabitaone.in</a>
        <div className="lang" role="group" aria-label={t('header.language')}>
          <span className="lang-ic" aria-hidden="true"><Icon name="globe" /></span>
          {LANGUAGES.map((l) => (
            <button key={l.code} type="button" lang={l.code} className={l.code === 'ml' ? 'ml' : undefined} aria-pressed={i18n.language === l.code} onClick={() => setLanguage(l.code)}>
              {l.label}
            </button>
          ))}
        </div>
      </div>
      <main className="auth-card">
        <div className="auth-mobile-brand"><Wordmark /></div>
        <Steps current={step.kind === 'identify' ? 0 : step.kind === 'code' ? 1 : 2} />
        {linked.data ? (
          <div className="linked-mahallu">
            {linked.data.markHash ? <img src={markUrl(linked.data.slug, linked.data.markHash)} alt="" /> : null}
            <div>
              <b>{linked.data.nameEn}</b>
              {linked.data.nameMl ? <span className="ml">{linked.data.nameMl}</span> : null}
            </div>
          </div>
        ) : null}

        {step.kind === 'identify' && (
          <form onSubmit={onIdentify} className="auth-form">
            <Badge icon={isConsole ? 'shieldCheck' : 'lock'} />
            <h1>{isConsole ? t('console.signin.title') : t('signin.title')}</h1>
            <p className="note">{isConsole ? t('console.signin.subtitle') : t('signin.subtitle')}</p>
            <div className="seg" role="tablist" aria-label={t('signin.identifier')}>
              {(['PHONE', 'EMAIL'] as const).map((k) => (
                <button key={k} type="button" role="tab" aria-selected={by === k} className={by === k ? 'on' : undefined} onClick={() => { setBy(k); setIdentifier(''); setError(''); }}>
                  <Icon name={k === 'PHONE' ? 'phone' : 'mail'} />
                  {t(k === 'PHONE' ? 'signin.byPhone' : 'signin.byEmail')}
                </button>
              ))}
            </div>
            {by === 'PHONE' ? (
              <label className="field" htmlFor="identifier">
                {t('signin.phone')}
                <span className="phone-field">
                  <span className="field-ic" aria-hidden="true"><Icon name="phone" /></span>
                  <select aria-label={t('signin.country')} value={cc} onChange={(e) => setCc(e.target.value)}>
                    {COUNTRY_CODES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <input id="identifier" type="tel" inputMode="tel" autoComplete="tel-national" autoFocus required value={identifier} onChange={(e) => setIdentifier(e.target.value)} />
                </span>
              </label>
            ) : (
              <label className="field" htmlFor="identifier">
                {t('signin.email')}
                <span className="icon-input">
                  <span className="field-ic" aria-hidden="true"><Icon name="mail" /></span>
                  <input id="identifier" type="email" inputMode="email" autoComplete="email" autoFocus required value={identifier} onChange={(e) => setIdentifier(e.target.value)} />
                </span>
              </label>
            )}
            {by === 'PHONE' && (
              <fieldset className="field">
                <legend>{t('signin.channel')}</legend>
                <div className="chips">
                  {(['WHATSAPP', 'SMS'] as const).map((c) => (
                    <button key={c} type="button" className={`chip ch-${c.toLowerCase()}`} aria-pressed={channel === c} onClick={() => setChannel(c)}>
                      <Icon name={c === 'WHATSAPP' ? 'whatsapp' : 'sms'} />
                      {t(`channel.${c}`)}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}
            <Feedback error={error} />
            <Submit busy={busy} disabled={!identifier.trim()} label={t('signin.sendCode')} busyLabel={t('signin.sending')} />
          </form>
        )}

        {step.kind === 'code' && (
          <form onSubmit={onCode} className="auth-form">
            <Badge icon={step.challenge.channel === 'EMAIL' ? 'mail' : step.challenge.channel === 'WHATSAPP' ? 'whatsapp' : 'sms'} />
            <h1>{t('signin.codeTitle')}</h1>
            <p className="note">{t('signin.codeSent', { channel: t(`channel.${step.challenge.channel}`), destination: step.challenge.destination })}</p>
            {import.meta.env.DEV ? <p className="callout small">{t('signin.devHint')}</p> : null}
            <CodeInput id="otp" label={t('signin.code')} value={code} onChange={setCode} autoComplete="one-time-code" />
            <Feedback error={error} />
            <Submit busy={busy} disabled={code.length !== 6} label={t('signin.continue')} />
            <div className="auth-links">
              {wait > 0 ? (
                <span className="note">{t('signin.resendIn', { n: wait })}</span>
              ) : (
                <button type="button" className="linkish" onClick={() => void send(step.challenge.channel)} disabled={busy}>
                  {t('signin.resend')}
                </button>
              )}
              {step.challenge.channel === 'WHATSAPP' && wait <= 0 ? (
                <button type="button" className="linkish" onClick={() => void send('SMS')} disabled={busy}>
                  {t('signin.trySms')}
                </button>
              ) : null}
              <button type="button" className="linkish" onClick={() => { setStep({ kind: 'identify' }); setError(''); }}>
                {t('signin.change')}
              </button>
            </div>
          </form>
        )}

        {step.kind === 'choose' && (
          <div className="auth-form">
            <Badge icon="building" />
            <h1>{t('signin.chooseTitle')}</h1>
            <p className="note">{step.mahallus.length ? t('signin.chooseBody') : t('console.signin.onlyConsole')}</p>
            {me.data?.operator || !step.mahallus.length ? (
              <a className="btn" href="/console/sign-in">
                {t('console.signin.open')}
              </a>
            ) : null}
            <div className="choice-list">
              {step.mahallus.map((m) => (
                <button key={m.slug} type="button" className="choice" disabled={busy} onClick={() => void run(async () => afterStep(await auth.chooseMahallu(m.slug)))}>
                  <span className="choice-ic" aria-hidden="true"><Icon name="building" /></span>
                  <span className="choice-txt">
                    <b>{i18n.language === 'ml' && m.nameMl ? m.nameMl : m.nameEn}</b>
                    <span className="note">{t(`role.${m.role}`)}</span>
                  </span>
                  <Icon name="chev" className="choice-go" />
                </button>
              ))}
            </div>
            <Feedback error={error} />
          </div>
        )}

        {step.kind === 'mfa' && <MfaStep enrolled={step.enrolled} busy={busy} error={error} run={run} afterStep={afterStep} />}
      </main>
      <p className="auth-foot">
        <Icon name="shieldCheck" />
        {t('signin.footer')}
      </p>
      </div>
    </div>
  );
}

function Feedback({ error }: { error: string }) {
  return error ? (
    <p className="form-error" role="alert">
      {error}
    </p>
  ) : null;
}

/** The three sign-in steps: number, code, authenticator (choosing a mahallu belongs to the last). */
function Steps({ current }: { current: number }) {
  const { t } = useTranslation();
  const labels = [t('signin.stepNumber'), t('signin.stepCode'), t('signin.stepVerify')];
  return (
    <ol className="auth-steps" aria-label={t('signin.steps')}>
      {labels.map((l, i) => (
        <li key={l} className={i < current ? 'done' : i === current ? 'on' : undefined} aria-current={i === current ? 'step' : undefined}>
          <span className="dot">{i < current ? <Icon name="check" /> : i + 1}</span>
          <span className="lbl">{l}</span>
        </li>
      ))}
    </ol>
  );
}

function Badge({ icon }: { icon: IconName }) {
  return (
    <span className="auth-badge" aria-hidden="true">
      <Icon name={icon} />
    </span>
  );
}

function Submit({ busy, disabled, label, busyLabel }: { busy: boolean; disabled: boolean; label: string; busyLabel?: string }) {
  return (
    <button type="submit" className="btn primary block auth-submit" disabled={busy || disabled} aria-busy={busy}>
      {busy ? <span className="spinner" aria-hidden="true" /> : null}
      <span>{busy && busyLabel ? busyLabel : label}</span>
      {busy ? null : <Icon name="arrow" />}
    </button>
  );
}

/** Six boxes for a six-digit code: typing moves on, backspace moves back, a pasted or auto-filled code fills all. */
function CodeInput({ id, label, value, onChange, autoComplete }: { id: string; label: string; value: string; onChange: (v: string) => void; autoComplete?: string }) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? '');
  const setFrom = (i: number, raw: string) => {
    const typed = raw.replace(/\D/g, '');
    if (!typed) return;
    const next = (value.slice(0, i) + typed).slice(0, 6);
    onChange(next);
    boxes.current[Math.min(next.length, 5)]?.focus();
  };
  return (
    <fieldset className="field code-field">
      <legend id={`${id}-label`}>{label}</legend>
      <div className="code-boxes" role="group" aria-labelledby={`${id}-label`}>
        {digits.map((d, i) => (
          <input
            key={i}
            id={i === 0 ? id : undefined}
            ref={(el) => {
              boxes.current[i] = el;
            }}
            className="code-box num"
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? autoComplete : 'off'}
            aria-label={`${label} ${i + 1}`}
            maxLength={i === 0 ? 6 : 1}
            autoFocus={i === 0}
            value={d}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => setFrom(i, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace') {
                e.preventDefault();
                const at = d ? i : Math.max(0, i - 1);
                onChange(value.slice(0, at) + value.slice(at + 1));
                boxes.current[at]?.focus();
              } else if (e.key === 'ArrowLeft') boxes.current[Math.max(0, i - 1)]?.focus();
              else if (e.key === 'ArrowRight') boxes.current[Math.min(5, i + 1)]?.focus();
            }}
            onPaste={(e) => {
              e.preventDefault();
              setFrom(0, e.clipboardData.getData('text'));
            }}
          />
        ))}
      </div>
    </fieldset>
  );
}

function MfaStep(props: { enrolled: boolean; busy: boolean; error: string; run: (fn: () => Promise<void>) => Promise<void>; afterStep: (r: SignInStepResponse) => Promise<void> }) {
  const { t } = useTranslation();
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const asked = useRef(false);

  useEffect(() => {
    if (props.enrolled || asked.current) return;
    asked.current = true;
    void props.run(async () => {
      const s = await auth.totpSetup();
      setSetup(s);
      setQr(await QRCode.toString(s.otpauthUri, { type: 'svg', margin: 1, width: 180 }));
    });
  }, [props]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void props.run(async () => {
      setCode('');
      await props.afterStep(await auth.totpVerify(code));
    });
  };

  return (
    <form onSubmit={onSubmit} className="auth-form">
      <Badge icon="key" />
      <h1>{t('mfa.title')}</h1>
      {props.enrolled ? (
        <p className="note">{t('mfa.enterBody')}</p>
      ) : (
        <>
          <p className="note">{t('mfa.setupBody')}</p>
          {setup ? (
            <div className="qr-box">
              {/* SVG produced locally by the qrcode library from our own otpauth link. */}
              <div className="qr" aria-label={t('mfa.title')} role="img" dangerouslySetInnerHTML={{ __html: qr }} />
              <div>
                <span className="note">{t('mfa.manualKey')}</span>
                <code className="secret">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
              </div>
            </div>
          ) : (
            <p className="note">{t('mfa.loading')}</p>
          )}
        </>
      )}
      <CodeInput id="totp" label={t('mfa.code')} value={code} onChange={setCode} autoComplete="one-time-code" />
      <Feedback error={props.error} />
      <Submit busy={props.busy} disabled={code.length !== 6 || (!props.enrolled && !setup)} label={t('mfa.verify')} />
    </form>
  );
}
